<script setup>
/**
 * Settings home.
 *
 * This page is an information-architecture layer over existing routes. It does
 * not fetch or mutate settings. Availability is resolved from each destination
 * route's own feature flag, permission and installation metadata, keeping the
 * landing page in lockstep with the router guards.
 */
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { usePolicy } from 'dashboard/composables/usePolicy';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import StatusChip from 'dashboard/components-next/status-chip/StatusChip.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { shouldShow } = usePolicy();

const searchQuery = ref('');
const pageHeading = ref(null);

const settingsGroups = computed(() => [
  {
    id: 'workspace',
    title: t('TOLA_SETTINGS_HOME.GROUPS.WORKSPACE.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.WORKSPACE.DESCRIPTION'),
    icon: 'i-lucide-building-2',
    items: [
      {
        routeName: 'general_settings_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.ACCOUNT.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.ACCOUNT.DESCRIPTION'),
        icon: 'i-lucide-settings-2',
      },
      {
        routeName: 'agent_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.AGENTS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.AGENTS.DESCRIPTION'),
        icon: 'i-lucide-square-user',
      },
      {
        routeName: 'office_hours_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.OFFICE_HOURS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.OFFICE_HOURS.DESCRIPTION'),
        icon: 'i-lucide-clock',
      },
      {
        routeName: 'security_settings_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.SECURITY.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.SECURITY.DESCRIPTION'),
        icon: 'i-lucide-shield',
      },
    ],
  },
  {
    id: 'subscription',
    title: t('TOLA_SETTINGS_HOME.GROUPS.SUBSCRIPTION.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.SUBSCRIPTION.DESCRIPTION'),
    icon: 'i-lucide-credit-card',
    items: [
      {
        routeName: 'billing_settings_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.BILLING.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.BILLING.DESCRIPTION'),
        icon: 'i-lucide-credit-card',
      },
    ],
  },
  {
    id: 'channels',
    title: t('TOLA_SETTINGS_HOME.GROUPS.CHANNELS.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.CHANNELS.DESCRIPTION'),
    icon: 'i-lucide-inbox',
    items: [
      {
        routeName: 'settings_inbox_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.CHANNELS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.CHANNELS.DESCRIPTION'),
        icon: 'i-lucide-inbox',
      },
    ],
  },
  {
    id: 'inbox',
    title: t('TOLA_SETTINGS_HOME.GROUPS.INBOX.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.INBOX.DESCRIPTION'),
    icon: 'i-lucide-messages-square',
    items: [
      {
        routeName: 'settings_teams_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.TEAMS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.TEAMS.DESCRIPTION'),
        icon: 'i-lucide-users',
      },
      {
        routeName: 'assignment_policy_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.ASSIGNMENT.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.ASSIGNMENT.DESCRIPTION'),
        icon: 'i-lucide-user-cog',
      },
      {
        routeName: 'macros_wrapper',
        title: t('TOLA_SETTINGS_HOME.ITEMS.MACROS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.MACROS.DESCRIPTION'),
        icon: 'i-lucide-toy-brick',
      },
      {
        routeName: 'canned_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.CANNED.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.CANNED.DESCRIPTION'),
        icon: 'i-lucide-message-square-quote',
      },
      {
        routeName: 'sla_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.SLA.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.SLA.DESCRIPTION'),
        icon: 'i-lucide-clock-alert',
      },
      {
        routeName: 'automation_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.AUTOMATIONS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.AUTOMATIONS.DESCRIPTION'),
        icon: 'i-lucide-repeat',
      },
      {
        routeName: 'conversation_workflow_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.CONVERSATION_WORKFLOW.TITLE'),
        description: t(
          'TOLA_SETTINGS_HOME.ITEMS.CONVERSATION_WORKFLOW.DESCRIPTION'
        ),
        icon: 'i-lucide-workflow',
      },
      {
        routeName: 'settings_ticket_types',
        title: t('TOLA_SETTINGS_HOME.ITEMS.TICKETS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.TICKETS.DESCRIPTION'),
        icon: 'i-lucide-ticket',
      },
    ],
  },
  {
    id: 'tola-ai',
    title: t('TOLA_SETTINGS_HOME.GROUPS.TOLA_AI.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.TOLA_AI.DESCRIPTION'),
    icon: 'i-woot-captain',
    items: [
      {
        routeName: 'captain_settings_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.AI.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.AI.DESCRIPTION'),
        icon: 'i-woot-captain',
      },
      {
        routeName: 'settings_mcp_connectors',
        title: t('TOLA_SETTINGS_HOME.ITEMS.MCP_CONNECTORS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.MCP_CONNECTORS.DESCRIPTION'),
        icon: 'i-lucide-plug',
      },
    ],
  },
  {
    id: 'data',
    title: t('TOLA_SETTINGS_HOME.GROUPS.DATA.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.DATA.DESCRIPTION'),
    icon: 'i-lucide-database',
    items: [
      {
        routeName: 'labels_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.LABELS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.LABELS.DESCRIPTION'),
        icon: 'i-lucide-tags',
      },
      {
        routeName: 'customer_attributes_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.PEOPLE_ATTRIBUTES.TITLE'),
        description: t(
          'TOLA_SETTINGS_HOME.ITEMS.PEOPLE_ATTRIBUTES.DESCRIPTION'
        ),
        icon: 'i-lucide-contact',
      },
      {
        routeName: 'attributes_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.ATTRIBUTES.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.ATTRIBUTES.DESCRIPTION'),
        icon: 'i-lucide-list-tree',
      },
      {
        routeName: 'settings_data_imports',
        title: t('TOLA_SETTINGS_HOME.ITEMS.DATA.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.DATA.DESCRIPTION'),
        icon: 'i-lucide-download',
      },
      {
        routeName: 'blocked_contacts_list',
        title: t('TOLA_SETTINGS_HOME.ITEMS.BLOCKED.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.BLOCKED.DESCRIPTION'),
        icon: 'i-lucide-ban',
      },
    ],
  },
  {
    id: 'integrations',
    title: t('TOLA_SETTINGS_HOME.GROUPS.INTEGRATIONS.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.INTEGRATIONS.DESCRIPTION'),
    icon: 'i-lucide-blocks',
    items: [
      {
        routeName: 'settings_applications',
        title: t('TOLA_SETTINGS_HOME.ITEMS.INTEGRATIONS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.INTEGRATIONS.DESCRIPTION'),
        icon: 'i-lucide-plug',
      },
      {
        routeName: 'settings_integrations_webhook',
        title: t('TOLA_SETTINGS_HOME.ITEMS.WEBHOOKS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.WEBHOOKS.DESCRIPTION'),
        icon: 'i-lucide-webhook',
      },
      {
        routeName: 'agent_bots',
        title: t('TOLA_SETTINGS_HOME.ITEMS.BOTS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.BOTS.DESCRIPTION'),
        icon: 'i-lucide-bot',
      },
      {
        routeName: 'settings_integrations_dashboard_apps',
        title: t('TOLA_SETTINGS_HOME.ITEMS.DASHBOARD_APPS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.DASHBOARD_APPS.DESCRIPTION'),
        icon: 'i-lucide-panels-top-left',
      },
    ],
  },
  {
    id: 'personal',
    title: t('TOLA_SETTINGS_HOME.GROUPS.PERSONAL.TITLE'),
    description: t('TOLA_SETTINGS_HOME.GROUPS.PERSONAL.DESCRIPTION'),
    icon: 'i-lucide-circle-user',
    items: [
      {
        routeName: 'notifications_settings_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.NOTIFICATIONS.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.NOTIFICATIONS.DESCRIPTION'),
        icon: 'i-lucide-bell',
      },
      {
        routeName: 'profile_settings_index',
        title: t('TOLA_SETTINGS_HOME.ITEMS.PROFILE.TITLE'),
        description: t('TOLA_SETTINGS_HOME.ITEMS.PROFILE.DESCRIPTION'),
        icon: 'i-lucide-circle-user',
      },
    ],
  },
]);

const routeTarget = item => ({
  name: item.routeName,
  params: { accountId: route.params.accountId },
});

const isAvailable = item => {
  const { meta } = router.resolve(routeTarget(item));
  return shouldShow(meta.featureFlag, meta.permissions, meta.installationTypes);
};

const groups = computed(() =>
  settingsGroups.value.map(group => ({
    ...group,
    availableItems: group.items.filter(isAvailable),
  }))
);

const selectedGroup = computed(() =>
  groups.value.find(group => group.id === route.query.group)
);

const normalizedSearch = computed(() => searchQuery.value.trim().toLowerCase());
const isSearching = computed(() => Boolean(normalizedSearch.value));

const searchResults = computed(() => {
  if (!normalizedSearch.value) return [];

  return groups.value.flatMap(group =>
    group.availableItems
      .filter(item =>
        [item.title, item.description, group.title].some(value =>
          value.toLowerCase().includes(normalizedSearch.value)
        )
      )
      .map(item => ({ ...item, groupTitle: group.title }))
  );
});

// The audit log is no longer a tile of its own (it lives on the Teammates
// page's Activity tab), but the recent-changes pointer still deep-links to it.
const auditLogsAvailable = computed(() =>
  isAvailable({ routeName: 'auditlogs_list' })
);

const showLanding = computed(() => !selectedGroup.value && !isSearching.value);

const openLanding = () => {
  searchQuery.value = '';
  router.push({
    name: 'settings_home',
    params: { accountId: route.params.accountId },
  });
};

watch(
  () => route.query.group,
  () => nextTick(() => pageHeading.value?.focus())
);
</script>

<template>
  <main
    class="w-full h-full overflow-y-auto bg-n-background"
    :aria-labelledby="
      selectedGroup ? 'settings-group-title' : 'settings-home-title'
    "
  >
    <div class="w-full max-w-6xl px-4 py-8 mx-auto sm:px-6 lg:px-8 lg:py-10">
      <header class="flex flex-col gap-2">
        <Button
          v-if="selectedGroup"
          class="self-start mb-2"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :label="t('TOLA_SETTINGS_HOME.BACK')"
          @click="openLanding"
        />
        <h1
          :id="selectedGroup ? 'settings-group-title' : 'settings-home-title'"
          ref="pageHeading"
          class="text-3xl font-semibold tracking-tight outline-none text-n-slate-12"
          tabindex="-1"
        >
          {{
            selectedGroup ? selectedGroup.title : t('TOLA_SETTINGS_HOME.TITLE')
          }}
        </h1>
        <p class="max-w-3xl mb-0 text-base leading-6 text-n-slate-11">
          {{
            selectedGroup
              ? selectedGroup.description
              : t('TOLA_SETTINGS_HOME.DESCRIPTION')
          }}
        </p>
      </header>

      <div class="relative max-w-2xl mt-6">
        <Input
          id="settings-search"
          v-model="searchQuery"
          type="search"
          size="md"
          :aria-label="t('TOLA_SETTINGS_HOME.SEARCH.LABEL')"
          :placeholder="t('TOLA_SETTINGS_HOME.SEARCH.PLACEHOLDER')"
          custom-input-class="!h-12 ltr:!pl-11 rtl:!pr-11 bg-n-solid-2"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute z-10 -translate-y-1/2 pointer-events-none size-4 top-1/2 text-n-slate-10 ltr:left-4 rtl:right-4"
            />
          </template>
        </Input>
      </div>

      <section
        v-if="showLanding"
        class="flex flex-col gap-3 p-4 mt-8 shadow-sm rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2 sm:flex-row sm:items-center"
        aria-labelledby="settings-recent-title"
      >
        <span
          aria-hidden="true"
          class="flex items-center justify-center rounded-lg size-10 shrink-0 bg-n-violet-3 text-n-violet-11"
        >
          <Icon icon="i-lucide-history" class="size-5" />
        </span>
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <h2
              id="settings-recent-title"
              class="text-sm font-semibold text-n-slate-12"
            >
              {{ t('TOLA_SETTINGS_HOME.RECENT.TITLE') }}
            </h2>
            <StatusChip
              status="unavailable"
              :reason="t('TOLA_SETTINGS_HOME.RECENT.REASON')"
              size="sm"
            />
          </div>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('TOLA_SETTINGS_HOME.RECENT.DESCRIPTION') }}
          </p>
        </div>
        <Button
          v-if="auditLogsAvailable"
          class="self-start sm:self-center"
          variant="outline"
          color="slate"
          size="sm"
          trailing-icon
          icon="i-lucide-arrow-right"
          :label="t('TOLA_SETTINGS_HOME.RECENT.AUDIT_LOG')"
          @click="router.push(routeTarget({ routeName: 'auditlogs_list' }))"
        />
      </section>

      <section
        v-if="showLanding"
        class="mt-10"
        aria-labelledby="settings-areas"
      >
        <div
          class="flex flex-col justify-between gap-1 mb-4 sm:flex-row sm:items-center"
        >
          <h2 id="settings-areas" class="text-heading-2 text-n-slate-12">
            {{ t('TOLA_SETTINGS_HOME.AREAS.TITLE') }}
          </h2>
          <span class="text-sm text-n-slate-11">
            {{ t('TOLA_SETTINGS_HOME.AREAS.COUNT') }}
          </span>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <RouterLink
            v-for="group in groups"
            v-show="group.availableItems.length"
            :key="group.id"
            :to="{
              name: 'settings_home',
              params: { accountId: route.params.accountId },
              query: { group: group.id },
            }"
            class="flex flex-col min-h-48 p-5 transition-all shadow-sm rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2 hover:-translate-y-0.5 hover:outline-n-violet-7 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          >
            <span
              aria-hidden="true"
              class="flex items-center justify-center rounded-xl size-10 bg-n-violet-3 text-n-violet-11"
            >
              <Icon :icon="group.icon" class="size-5" />
            </span>
            <h3 class="mt-5 text-base font-semibold text-n-slate-12">
              {{ group.title }}
            </h3>
            <p class="mt-1 mb-4 text-sm leading-5 text-n-slate-11">
              {{ group.description }}
            </p>
            <span
              class="inline-flex items-center gap-1 mt-auto text-xs font-semibold text-n-violet-11"
            >
              {{
                t('TOLA_SETTINGS_HOME.AREAS.AVAILABLE_COUNT', {
                  count: group.availableItems.length,
                })
              }}
              <Icon icon="i-lucide-arrow-right" class="size-3.5" />
            </span>
          </RouterLink>

          <article
            v-for="group in groups"
            v-show="!group.availableItems.length"
            :key="`${group.id}-unavailable`"
            class="flex flex-col min-h-48 p-5 shadow-sm rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2 opacity-70"
            aria-disabled="true"
          >
            <span
              aria-hidden="true"
              class="flex items-center justify-center rounded-xl size-10 bg-n-slate-3 text-n-slate-11"
            >
              <Icon :icon="group.icon" class="size-5" />
            </span>
            <h3 class="mt-5 text-base font-semibold text-n-slate-12">
              {{ group.title }}
            </h3>
            <p class="mt-1 mb-4 text-sm leading-5 text-n-slate-11">
              {{ group.description }}
            </p>
            <StatusChip
              class="self-start mt-auto"
              status="unavailable"
              :reason="t('TOLA_SETTINGS_HOME.AREAS.UNAVAILABLE_REASON')"
              size="sm"
            />
          </article>
        </div>
      </section>

      <section
        v-else
        class="mt-8"
        :aria-labelledby="
          isSearching ? 'settings-results-title' : 'settings-group-items-title'
        "
      >
        <div class="flex flex-col gap-1 mb-4">
          <h2
            :id="
              isSearching
                ? 'settings-results-title'
                : 'settings-group-items-title'
            "
            class="text-heading-2 text-n-slate-12"
          >
            {{
              isSearching
                ? t('TOLA_SETTINGS_HOME.SEARCH.RESULTS')
                : t('TOLA_SETTINGS_HOME.GROUP_ITEMS')
            }}
          </h2>
          <p
            v-if="isSearching"
            class="mb-0 text-sm text-n-slate-11"
            role="status"
          >
            {{
              t('TOLA_SETTINGS_HOME.SEARCH.COUNT', {
                count: searchResults.length,
              })
            }}
          </p>
        </div>

        <div
          v-if="isSearching && !searchResults.length"
          class="flex flex-col items-center justify-center gap-2 py-16 text-center rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
        >
          <Icon icon="i-lucide-search-x" class="size-8 text-n-slate-10" />
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('TOLA_SETTINGS_HOME.SEARCH.EMPTY_TITLE') }}
          </h3>
          <p class="max-w-md mb-0 text-sm text-n-slate-11">
            {{ t('TOLA_SETTINGS_HOME.SEARCH.EMPTY_DESCRIPTION') }}
          </p>
        </div>

        <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-2">
          <RouterLink
            v-for="item in isSearching
              ? searchResults
              : selectedGroup.availableItems"
            :key="`${item.routeName}-${item.title}`"
            :to="routeTarget(item)"
            class="flex items-start gap-3 p-4 shadow-sm rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container hover:outline-n-violet-7 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          >
            <span
              aria-hidden="true"
              class="flex items-center justify-center rounded-lg size-10 shrink-0 bg-n-violet-3 text-n-violet-11"
            >
              <Icon :icon="item.icon" class="size-5" />
            </span>
            <span class="flex flex-col min-w-0 gap-1">
              <span class="flex items-center gap-2">
                <strong class="text-sm text-n-slate-12">
                  {{ item.title }}
                </strong>
                <span
                  v-if="isSearching"
                  class="text-xs truncate text-n-slate-10"
                >
                  {{ item.groupTitle }}
                </span>
              </span>
              <span class="text-sm leading-5 text-n-slate-11">
                {{ item.description }}
              </span>
            </span>
            <Icon
              icon="i-lucide-chevron-right"
              class="mt-1 ml-auto size-4 shrink-0 text-n-slate-10"
            />
          </RouterLink>
        </div>
      </section>

      <aside
        v-if="showLanding"
        class="flex items-start gap-3 p-4 mt-8 rounded-xl bg-n-amber-2 outline outline-1 -outline-offset-1 outline-n-amber-6"
        aria-labelledby="settings-safety-title"
      >
        <Icon
          icon="i-lucide-shield-alert"
          class="mt-0.5 size-5 shrink-0 text-n-amber-11"
        />
        <div>
          <h2
            id="settings-safety-title"
            class="text-sm font-semibold text-n-amber-11"
          >
            {{ t('TOLA_SETTINGS_HOME.SAFETY.TITLE') }}
          </h2>
          <p class="mt-1 mb-0 text-sm leading-5 text-n-slate-11">
            {{ t('TOLA_SETTINGS_HOME.SAFETY.DESCRIPTION') }}
          </p>
        </div>
      </aside>
    </div>
  </main>
</template>
